import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { withTranslation } from 'react-i18next'
import Box from '../../../Shared/Box'
import EditBasic from './EditBasic'
import EditMappings from './EditMappings'
import { Col, Container, Row } from '../../../Shared/Grid'
import { getFactsheetBlueprint, putGiataHotelierProperty } from '../../../../api'
import Alert from '../../../Shared/Alert'
import Button from '../../../Shared/Form/Button'
import './styles.scss'
import isequal from 'lodash.isequal'
import Factsheet from '../../../Shared/Factsheet'
import { factsheetActions } from '../../../../redux/actions/factsheet'
import { connect } from 'react-redux'
import debounce from 'lodash.debounce'
import activityTrackingService from '../../../../services/activityTrackingService'
import { trackingEvent } from '../../../../constants'
import PageHead from '../../../Shared/PageHead'
import PublishAllButton from '../PublishAllButton'
import Loader from '../../../Shared/Loader'
import { FACT_ATTRIBUTE_IDS, FACT_IDS } from '../../../Shared/Factsheet/constants'

const propertyInfoStatus = {
  STATUS_NOT_APPLICABLE: 1,
  STATUS_NOT_CONFIRMED: 2,
  STATUS_CONFIRMED: 3
}

const PROPERTY_DATA_SECTION_KEYS = {
  BASIC: 1,
  MAPPINGS: 2
}

const ALLOWED_MAPPINGS = [
  {
    id: 754,
    name: 'Amadeus GDS'
  },
  {
    id: 463,
    name: 'Sabre'
  },
  {
    id: 547,
    name: 'Travelport Worldspan'
  },
  {
    id: 548,
    name: 'Travelport Galileo'
  },
  {
    id: 761,
    name: 'RateGain'
  },
  {
    id: 27,
    name: 'Booking.com',
  },
  {
    id: 680,
    name: 'Expedia', // Expedia EAN Rapid
  },
  {
    id: 29,
    name: 'TripAdvisor',
  }
]

class EditProperty extends PureComponent {

  state = {
    loading: false,
    giataHotelierProperty: null,
    originalGiataHotelierProperty: null,
    originalFactsheet: null,
    factsheetBlueprint: null,
    error: null,
    validBasic: false,
    validMappings: false,
    isDirty: false,
  }
  abortController = null

  componentDidMount () {
    activityTrackingService.trackPageView('Edit Property', {
      giataId: this.props.giataId,
    })
    this.init()
  }

  componentDidUpdate (prevProps, prevState) {
    if (prevProps.giataId !== this.props.giataId) {
      this.init()
      return
    }
    if (
      (prevProps.factsheetFacts !== this.props.factsheetFacts) ||
      (prevState.giataHotelierProperty !== this.state.giataHotelierProperty) ||
      (prevState.originalGiataHotelierProperty !== this.state.originalGiataHotelierProperty) ||
      (prevState.originalFactsheet !== this.state.originalFactsheet)
    ) {
      this.setIsDirtyDebounced()
    }
  }

  componentWillUnmount () {
    if (this.abortController !== null) {
      this.abortController.abort()
    }
    this.props.onIsDirtyChange(false)
  }

  onPropertyChange = (newProperty) => {
    this.setState({ giataHotelierProperty: newProperty })
  }

  onMappingsChange = (mappings) => {
    const giataHotelierProperty = { ...this.state.giataHotelierProperty }
    giataHotelierProperty.mappings = mappings
    this.setState({ giataHotelierProperty })
  }

  onValidationChange = (propertyDataSectionKey, isValid) => {
    switch (propertyDataSectionKey) {
      case PROPERTY_DATA_SECTION_KEYS.BASIC:
        this.setState({ validBasic: isValid })
        break
      case PROPERTY_DATA_SECTION_KEYS.MAPPINGS:
        this.setState({ validMappings: isValid })
        break
      default:
        throw new Error('invalid propertyDataSectionKey')
    }
  }

  init () {
    this.props.putFactsheet({ facts: {} })
    this.setState({ giataHotelierProperty: null, loading: true, error: null }, async () => {
        if (this.abortController !== null) {
          this.abortController.abort()
        }
        this.abortController = new AbortController()
        const request = getFactsheetBlueprint(this.abortController)
        let factsheetBlueprintRes
        try {
          factsheetBlueprintRes = await request.fetchPromise
        } catch (e) {
          if (!this.abortController.signal.aborted) {
            this.setState({ loading: false, error: true })
          }
          return
        }
        let originalFactsheet = { facts: {} }
        const giataHotelierProperty = { ...this.props.propertyInfo }
        if (giataHotelierProperty.hasOwnProperty('factsheet')) {
          originalFactsheet = giataHotelierProperty.factsheet
          delete giataHotelierProperty.factsheet
        }
        if (giataHotelierProperty.country?.code) {
          this.props.initialFactsheet && this.putCountryFact(this.props.initialFactsheet, giataHotelierProperty.country.code)
          originalFactsheet && this.putCountryFact(originalFactsheet, giataHotelierProperty.country.code)
        }
        this.props.putFactsheet(this.props.initialFactsheet ? this.props.initialFactsheet : originalFactsheet)
        const factsheetBlueprint = {
          ...factsheetBlueprintRes.factsheet
        }
        factsheetBlueprint.factTree = factsheetBlueprintRes.factTree
        this.props.putBlueprint(structuredClone(factsheetBlueprint))
        this.setState({
          giataHotelierProperty: giataHotelierProperty,
          originalGiataHotelierProperty: giataHotelierProperty,
          originalFactsheet,
          factsheetBlueprint,
          loading: false,
          isDirty: false
        })
        this.props.onIsDirtyChange(false)
      }
    )
  }

  isSaveAllowed () {
    return this.state.validBasic && this.state.validMappings && this.props.factsheetIsValid
  }

  hasUnconfirmedInfo () {
    if (!this.isStatus('propertyInfo', propertyInfoStatus.STATUS_CONFIRMED)) {
      return true
    }
    if (!this.areAllMappingsConfirmed()) {
      return true
    }
    return false
  }

  areAllMappingsConfirmed () {
    if (this.state.giataHotelierProperty.hasOwnProperty('status') === false) {
      return false
    }
    for (let mapping of ALLOWED_MAPPINGS) {
      if (!this.state.giataHotelierProperty.status.mappings.hasOwnProperty(mapping.id)) {
        return false
      }
      if (this.state.giataHotelierProperty.status.mappings[mapping.id] !== propertyInfoStatus.STATUS_CONFIRMED) {
        return false
      }
    }
    return true
  }

  isStatus (name, status) {
    if (
      this.state.giataHotelierProperty.hasOwnProperty('status') === false
      || this.state.giataHotelierProperty.status.hasOwnProperty(name) === false
    ) {
      return false
    }
    return this.state.giataHotelierProperty.status[name] === status

  }

  setIsDirtyDebounced = debounce(() => {
    // makes no sense to perform the check if the propertyInfo values are null
    if (
      this.state.giataHotelierProperty === null
      || this.state.originalGiataHotelierProperty === null
    ) {
      return
    }
    const isDirty = !isequal(this.state.giataHotelierProperty, this.state.originalGiataHotelierProperty)
      || !isequal(this.props.factsheetFacts, this.state.originalFactsheet?.facts)

    this.setState({ isDirty })
    this.props.onIsDirtyChange(isDirty)
  }, 1000, { leading: true })

  onSave = () => {
    if (!this.isSaveAllowed()) {
      return
    }
    if (this.abortController !== null) {
      this.abortController.abort()
    }
    this.abortController = new AbortController()
    const body = { ...this.state.giataHotelierProperty }
    // if (!this.isDeployToIcePortal()) {
    //   body.mappings = []
    // }
    activityTrackingService.trackEvent(trackingEvent.saveFactsheet, {
      giataId: this.props.giataId,
    })
    body.factsheet = { facts: this.props.factsheetFacts }
    this.setState({ loading: true, error: null }, () => {
      const { fetchPromise } = putGiataHotelierProperty(
        this.props.giataId, body,
        this.abortController
      )
      fetchPromise.then((response) => {
        const newGiataHotelierProperty = response
        this.props.updatePropertyInfo && this.props.updatePropertyInfo({ ...newGiataHotelierProperty })
        let newFactsheet = { facts: {} }
        if (response.hasOwnProperty('factsheet')) {
          newFactsheet = response.factsheet
          delete newGiataHotelierProperty.factsheet
        }
        this.setState({
          loading: false,
          originalGiataHotelierProperty: newGiataHotelierProperty,
          giataHotelierProperty: newGiataHotelierProperty,
          originalFactsheet: newFactsheet,
        }, () => {
          this.props.putFactsheet(newFactsheet)
        })
      }).catch(() => {
        this.setState({
          loading: false, error: true
        })
        window.scrollTo(0, 0)
      })
    })
  }

  render () {
    const { t } = this.props
    const saveAllowed = this.isSaveAllowed()
    const saveEnabled =
      !this.state.loading
      && !this.props.propertyPublishInProgress
      && saveAllowed
      && (
        // property needs initial confirmation
        this.state.giataHotelierProperty?.status?.propertyInfo === 2
        || this.state.isDirty
        || this.hasUnconfirmedInfo()
      )
    return (<div className={'page page-edit-property'}>
        {this.state.loading && <Loader size={32}/>}
        {this.state.error && <Alert type={'danger'}>Error occurred</Alert>}
        {
          this.state.giataHotelierProperty &&
          <Container fluid gutterWidth={5}>
            <form>
              <Box>
                <Row>
                  <Col xs={12}>
                    <PageHead
                      marginBottom={37}
                      title={t('PROPERTY_INFORMATION')}
                      subtitle="This is the information we have of your property."
                    >
                      <Button
                        disabled={!saveEnabled}
                        onClick={this.onSave}
                        classNames={!saveEnabled ? 'Button-Primary-v2 Button-Min-Width Button-Icon-Save-Grey' : 'Button-Primary-v2 Button-Min-Width Button-Icon-Save'}
                      >
                        {t('BUTTON_SAVE')}
                      </Button>
                      <PublishAllButton publishAllowed={!this.state.isDirty} giataId={this.props.giataId}/>
                    </PageHead>
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} md={6}>
                    <EditBasic giataHotelierProperty={this.state.giataHotelierProperty}
                               onPropertyChange={this.onPropertyChange}
                               onValidationChange={(isValid) => this.onValidationChange(PROPERTY_DATA_SECTION_KEYS.BASIC, isValid)}
                    />
                  </Col>
                  <Col xs={12} md={6}>
                    <EditMappings mappings={this.state.giataHotelierProperty.mappings}
                                  allowedMappings={ALLOWED_MAPPINGS}
                                  onMappingsChange={this.onMappingsChange}
                                  onValidation={(isValid) => this.onValidationChange(PROPERTY_DATA_SECTION_KEYS.MAPPINGS, isValid)}
                    />
                  </Col>
                </Row>
              </Box>
              <div style={{ maxWidth: '1240px' }}>
                <Row>
                  <Col>
                    <PageHead
                      marginBottom={37}
                      title={t('FACTSHEET')}
                      subtitle={t('FACTSHEET_SUB_HEADER')}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <div style={{ minHeight: '100vh' }}>
                      <Factsheet blueprint={this.state.factsheetBlueprint}/>
                    </div>
                  </Col>
                </Row>
              </div>
            </form>
          </Container>
        }
      </div>
    )
  }

  putCountryFact (factsheet, countryCode) {
    if (!factsheet.facts) {
      return
    }
    if (factsheet.facts[String(FACT_IDS.COUNTRY)] !== undefined) {
      return
    }
    factsheet.facts[String(FACT_IDS.COUNTRY)] = [{
      factDefId: String(FACT_IDS.COUNTRY),
      attributes: {
        [String(FACT_ATTRIBUTE_IDS.COUNTRY_CODE)]: {
          attributeDefId: String(FACT_ATTRIBUTE_IDS.COUNTRY_CODE),
          value: countryCode,
        }
      }
    }]
  }
}

EditProperty.propTypes = {
  giataId: PropTypes.number.isRequired,
  permissions: PropTypes.object.isRequired,
  onIsDirtyChange: PropTypes.func.isRequired,
  propertyInfo: PropTypes.object,
  initialFactsheet: PropTypes.object,
  updatePropertyInfo: PropTypes.func,
}

const mapStateToProps = (state) => {
  return {
    factsheetFacts: state.factsheet.facts,
    factsheetIsValid: state.factsheet.validation.isValid,
    propertyPublishInProgress: state.property.publishInProgress,
  }
}

const mapDispatchToProps = {
  putBlueprint: factsheetActions.putBlueprint,
  putFactsheet: factsheetActions.putFactsheet,
}
export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(EditProperty))
